// Browser-local UI preferences — model, reasoning effort, display name. Lives in
// localStorage. NOTE: the Anthropic API key used to live here, but it's now stored
// encrypted server-side (per account) and used for server-side builds — see
// backend/src/routes/auth.ts (/api/account/api-key) and the run endpoints.

const KEY_MODEL = "quikpl.studio.model";
const KEY_NAME = "quikpl.studio.display_name";
const KEY_REASONING = "quikpl.studio.reasoning";

// NOTE: user_id used to live here, but it's now a server-set HttpOnly-ish cookie
// (`quikpl_user_id`) so it survives localStorage clears, dev-tools "clear site data",
// and different browser sessions. The Studio reads it from /api/me if it needs to
// display the current identity. See backend/src/auth.ts.

export const DEFAULT_MODEL = "claude-sonnet-4-6";

/** Extended-thinking budget, in tokens. `off` disables thinking entirely (fastest,
 *  cheapest). `medium` is the recommended default — gives the model enough room to
 *  plan a multi-phase party game without ballooning cost. `high` is for one-shotting
 *  genuinely complex builds (multi-state-machine games, dense data wrangling). */
export type ReasoningLevel = "off" | "low" | "medium" | "high";
export const DEFAULT_REASONING: ReasoningLevel = "medium";

export function getModel(): string {
  const stored = localStorage.getItem(KEY_MODEL);
  // 'claude-sonnet-4-7' was a bad ID that shipped briefly (Anthropic 404s it). Any
  // user who picked it has it pinned in localStorage and inference would 404 every
  // time — coerce them back to the default the first time we read it back.
  if (!stored || stored === "claude-sonnet-4-7") return DEFAULT_MODEL;
  return stored;
}
export function setModel(m: string): void {
  localStorage.setItem(KEY_MODEL, m);
}

export function getDisplayName(): string {
  return localStorage.getItem(KEY_NAME) || "";
}
export function setDisplayName(s: string): void {
  if (!s) localStorage.removeItem(KEY_NAME);
  else localStorage.setItem(KEY_NAME, s);
}

const VALID_REASONING: readonly ReasoningLevel[] = ["off", "low", "medium", "high"];
export function getReasoning(): ReasoningLevel {
  const v = localStorage.getItem(KEY_REASONING) as ReasoningLevel | null;
  return v && VALID_REASONING.includes(v) ? v : DEFAULT_REASONING;
}
export function setReasoning(level: ReasoningLevel): void {
  localStorage.setItem(KEY_REASONING, level);
}
